package com.evoapps.evoshift.domain.usecase.requests

import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.AuditMetadata
import com.evoapps.evoshift.domain.model.NotificationData
import com.evoapps.evoshift.domain.model.RequestSettings
import com.evoapps.evoshift.domain.model.RequestPlace
import com.evoapps.evoshift.domain.model.WorkRequest
import com.evoapps.evoshift.domain.model.WorkRequestKind
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkRequestRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.requests.LimitCheck
import com.evoapps.evoshift.domain.requests.RequestCounters
import com.evoapps.evoshift.domain.usecase.ReportAbsenceUseCase
import com.evoapps.evoshift.domain.util.AppClock

/** SH-223: a work request that is not complete ([what] is missing or wrong). */
class InvalidWorkRequestException(val what: String) : IllegalArgumentException("Work request: $what")

/**
 * SH-223: an employee asks for remote work, a work event or overtime: PENDING, the managers are
 * notified (as for absences, the type unnamed). Overtime needs its hours; remote work its place.
 * The place is the shared list's ([RequestPlace]); its name is kept on the request.
 */
class SubmitWorkRequestUseCase(
    private val repository: WorkRequestRepository,
    private val userRepository: UserRepository,
    /** SH-223: the workplace's rules of the types; without it every type is offered. */
    private val workplaceRepository: WorkplaceRepository? = null
) {
    /** SH-223: [actingUserId] a manager filing it for [WorkRequest.userId]; [approveNow] approves it at once. */
    suspend operator fun invoke(draft: WorkRequest, actingUserId: String = draft.userId, approveNow: Boolean = false): Result<String> = runCatching {
        val note = draft.note?.trim()?.takeIf { it.isNotEmpty() }
        if (note != null && note.length > ReportAbsenceUseCase.REASON_MAX_LENGTH) throw InvalidWorkRequestException("note")
        if (draft.endDate < draft.startDate) throw InvalidWorkRequestException("dates")
        val hourly = draft.isHourly
        if (draft.kind == WorkRequestKind.OVERTIME && !hourly) throw InvalidWorkRequestException("hours")
        if (hourly && (RequestCounters.time(draft.startTime!!) == null || RequestCounters.time(draft.endTime!!) == null)) throw InvalidWorkRequestException("hours")
        if (draft.kind == WorkRequestKind.REMOTE_WORK && draft.placeName.isNullOrBlank()) throw InvalidWorkRequestException("place")
        workplaceRepository?.getWorkplace(draft.workplaceId)?.getOrNull()?.let { workplace ->
            val financial = userRepository.getSensitiveUserData(draft.userId).getOrNull()?.financialEngagements?.get(workplace.id)
            if (!com.evoapps.evoshift.domain.requests.RequestEligibility.allows(
                    workplace.settings.requests, com.evoapps.evoshift.domain.requests.RequestType.Work(draft.kind), financial, draft.startDate)
            ) throw com.evoapps.evoshift.domain.requests.RequestNotAllowedException(draft.kind.name)
        }
        val onBehalf = actingUserId != draft.userId
        val approved = approveNow && onBehalf
        val nowStr = AppClock.now().toString()
        val request = draft.copy(
            id = "",
            status = if (approved) AbsenceStatus.APPROVED else AbsenceStatus.PENDING,
            note = note,
            startTime = draft.startTime?.takeIf { hourly },
            endTime = draft.endTime?.takeIf { hourly },
            placeName = draft.placeName?.trim()?.takeIf { it.isNotEmpty() },
            rejectionReason = null, processedBy = actingUserId.takeIf { approved }, processedAt = nowStr.takeIf { approved },
            metadata = AuditMetadata(createdAt = nowStr, createdBy = actingUserId)
        )
        val id = repository.create(request).getOrThrow()
        if (onBehalf) {
            ReportAbsenceUseCase.notifyOnBehalf(userRepository, request.workplaceId, request.userId, actingUserId, request.startDate, request.endDate,
                id, NotificationData.WORK_REQUEST_ID, approved)
        } else {
            ReportAbsenceUseCase.notifyManagers(userRepository, request.workplaceId, request.userId, request.startDate, request.endDate, id, NotificationData.WORK_REQUEST_ID)
        }
        id
    }
}

/** SH-223: a manager approves or rejects a work request (the person's shifts stay as they are). */
class DecideWorkRequestUseCase(private val repository: WorkRequestRepository) {
    suspend operator fun invoke(request: WorkRequest, status: AbsenceStatus, managerId: String, managerName: String?, rejectionReason: String? = null): Result<WorkRequest> {
        if (status == AbsenceStatus.PENDING) return Result.failure(IllegalArgumentException("A decision approves or rejects"))
        val now = AppClock.now().toString()
        val decided = request.copy(
            status = status,
            rejectionReason = rejectionReason.takeIf { status == AbsenceStatus.REJECTED },
            processedBy = managerId,
            processedAt = now,
            metadata = request.metadata.copy(updatedAt = now, updatedBy = managerName ?: managerId)
        )
        return repository.update(decided).map { decided }
    }
}

/**
 * SH-223: the workplace's shared places to work from. Anyone at the workplace adds one (a name
 * already on the list, in any letter case, is that place); managers rename and remove them.
 */
class WorkPlacesUseCase(private val repository: WorkRequestRepository) {
    suspend fun list(workplaceId: String): Result<List<RequestPlace>> = repository.places(workplaceId)

    suspend fun add(workplaceId: String, name: String, userId: String, existing: List<RequestPlace>): Result<RequestPlace> {
        val clean = name.trim().replace(Regex("\\s+"), " ").take(MAX_NAME)
        if (clean.isEmpty()) return Result.failure(InvalidWorkRequestException("place"))
        existing.firstOrNull { it.name.equals(clean, ignoreCase = true) }?.let { return Result.success(it) }
        return repository.addPlace(workplaceId, RequestPlace(id = "", name = clean, createdBy = userId, createdAt = AppClock.now().toString()))
    }

    suspend fun rename(workplaceId: String, placeId: String, name: String): Result<Unit> {
        val clean = name.trim().replace(Regex("\\s+"), " ").take(MAX_NAME)
        if (clean.isEmpty()) return Result.failure(InvalidWorkRequestException("place"))
        return repository.renamePlace(workplaceId, placeId, clean)
    }

    suspend fun remove(workplaceId: String, placeId: String): Result<Unit> = repository.removePlace(workplaceId, placeId)

    companion object {
        const val MAX_NAME = 80

        /**
         * The places in picking order: those [userId] used, most recent first, then the rest by
         * name. A removed place the person used is not offered again.
         */
        fun ordered(places: List<RequestPlace>, userId: String, requests: List<WorkRequest>): List<RequestPlace> {
            val mine = requests.filter { it.userId == userId && it.placeId != null }
                .sortedByDescending { it.metadata.createdAt }
                .mapNotNull { it.placeId }.distinct()
            val byId = places.associateBy { it.id }
            val recent = mine.mapNotNull { byId[it] }
            return recent + places.filter { it !in recent }.sortedBy { it.name.lowercase() }
        }

        /** How many of [ordered]'s first are the person's own recent ones. */
        fun recentCount(places: List<RequestPlace>, userId: String, requests: List<WorkRequest>): Int {
            val ids = places.map { it.id }.toSet()
            return requests.filter { it.userId == userId && it.placeId in ids }.mapNotNull { it.placeId }.distinct().size
        }
    }
}

/**
 * SH-223: before a manager approves an absence with a yearly limit, the person's requests read
 * again (a colleague may have approved another meanwhile) and the limit checked against the
 * approved ones. [LimitCheck.Exceeds] asks the manager for an override note.
 */
class CheckApprovalLimitUseCase(
    private val absenceRepository: AbsenceRepository,
    private val workplaceRepository: WorkplaceRepository? = null,
    private val workRequestRepository: WorkRequestRepository? = null
) {
    suspend operator fun invoke(absence: Absence): LimitCheck {
        if (absence.type !in ReportAbsenceUseCase.LIMITED_TYPES) return LimitCheck.Ok
        val workplaceId = absence.workplaceId ?: return LimitCheck.Ok
        val workplace = workplaceRepository?.getWorkplace(workplaceId)?.getOrNull()
        val settings = workplace?.settings?.requests ?: RequestSettings()
        val rules = PayrollRules.of(workplace?.settings?.market ?: Market.POLAND)
        val theirs = absenceRepository.getAbsencesForWorkplace(workplaceId).getOrDefault(emptyList()).filter { it.userId == absence.userId }
        val overtime = if (absence.type == com.evoapps.evoshift.domain.model.AbsenceType.TIME_OFF_IN_LIEU) {
            workRequestRepository?.forWorkplace(workplaceId)?.getOrNull().orEmpty().filter { it.userId == absence.userId }
        } else emptyList()
        return RequestCounters.check(absence, theirs, settings, rules, overtime, againstApproved = true)
    }
}
